import './AntragField.css';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  isNodeEmpty,
  NodeType,
  PresentationNode
} from '../../api/models/Case/PresentationModel';
import { AntragFieldV2 } from './AntragFieldV2';

interface AntragNodeV2Props {
  node: PresentationNode;
  depthLevel: number;
  hideEmpty?: boolean;
  suppressLabel?: boolean;
}

export function AntragNodeV2({
  node,
  depthLevel,
  hideEmpty,
  suppressLabel
}: AntragNodeV2Props) {
  const { t } = useTranslation();
  if (hideEmpty && isNodeEmpty(node)) {
    return null;
  }

  switch (node.nodeType) {
    case NodeType.String:
    case NodeType.Date:
      return (
        <AntragFieldV2
          label={node.label}
          value={node.value}
          suppressLabel={suppressLabel}
        />
      );

    case NodeType.Boolean:
      return (
        <AntragFieldV2
          label={node.label}
          value={node.value === null ? null : node.value ? t('antragPrint.boolean.yes') : t('antragPrint.boolean.no')}
          suppressLabel={suppressLabel}
        />
      );

    case NodeType.Select:
      return (
        <AntragFieldV2
          label={node.label}
          value={node.selectedDisplayLabel}
          suppressLabel={suppressLabel}
        />
      );

    case NodeType.File: {
      const displayValue = node.fileName ?? node.fileId;
      const description = node.description
        ? ` (Dokument: ${node.description})`
        : '';
      const fullValue = displayValue ? `${displayValue}${description}` : null;
      return (
        <AntragFieldV2
          label={node.label}
          value={fullValue}
          suppressLabel={suppressLabel}
        />
      );
    }

    case NodeType.Group: {
      let entries = Object.entries(node.children);
      if (hideEmpty) {
        entries = entries.filter(([, child]) => !isNodeEmpty(child));
      }
      if (entries.length === 0) return null;

      return (
        <div className="antrag-page__container">
          <div className="antrag-field__group">
            <label className="antrag-field__label antrag-field__label--group">
              {node.label}
            </label>
            <div className="antrag-field__nested">
              {entries.map(([key, child]) => (
                <AntragNodeV2
                  key={key}
                  node={child}
                  depthLevel={depthLevel + 1}
                  hideEmpty={hideEmpty}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    case NodeType.Array: {
      let items = node.items;
      if (hideEmpty) {
        items = items.filter((item) => !isNodeEmpty(item));
      }

      if (items.length === 0) {
        if (hideEmpty) return null;
        if (node.label) {
          return (
            <AntragFieldV2
              label={node.label}
              value={null}
              suppressLabel={suppressLabel}
            />
          );
        }
        return null;
      }

      const allSameLabel =
        items.length > 0 &&
        items.every((item) => item.label === items[0].label);

      const allSimpleTypes =
        items.length > 0 &&
        items.every(
          (item) =>
            item.nodeType === NodeType.String ||
            item.nodeType === NodeType.File
        );

      const suppressLabels = allSimpleTypes && allSameLabel;

      return (
        <div className="antrag-page__container">
          <div className="antrag-field__group">
            {suppressLabels && (
              <label className="antrag-field__label antrag-field__label--group">
                {items[0].label}
              </label>
            )}
            <div className="antrag-field__nested">
              {items.map((item) => (
                <AntragNodeV2
                  key={item.label}
                  node={item}
                  depthLevel={depthLevel + 1}
                  hideEmpty={hideEmpty}
                  suppressLabel={suppressLabels}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }
  }
}
